import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Link, Navigate, useNavigate, useParams } from 'react-router-dom';
import { camerasAPI, logsAPI, type PlateDirection } from '../../utils/api';
import { mergeCategoryLogs } from '../../utils/categoryLogs';
import { cameraHasCatalogModel, useModelsCatalog } from '../../contexts/ModelsContext';
import { useAreasNav } from '../../contexts/AreasContext';
import { useSubscription } from '../../contexts/SubscriptionContext';
import LogsDateRangePicker from '../../components/LogsDateRangePicker/LogsDateRangePicker';
import DetectionPhotoModal from '../../components/DetectionPhotoModal/DetectionPhotoModal';
import ModelIcon from '../../components/ModelIcon/ModelIcon';
import { getModelDisplayName } from '../../utils/modelLabels';
import { hasLicensePlateRoi, isLicensePlateModel } from '../../utils/directionVector';
import { DirectionBadge } from '../../components/DirectionBadge/DirectionBadge';
import { isPresenceCheckModel } from '../../utils/presence';
import '../Plates/Plates.css';
import './ModelCategory.css';

const PAGE_SIZE = 50;
const POLL_MS = 10_000;

interface CategoryCamera {
  id: number;
  name: string;
  areaId: number;
  areaName: string;
  sourceId: string | null;
  models: string[];
  status: any;
  isOnline: boolean;
  webrtcOut: string | null;
  isPaid: boolean;
  paidUntil: string | null;
}

interface CategoryLog {
  id: string | number;
  timestamp: string;
  model: string;
  screen_url: string | null;
  camera_id: string | number | null;
  camera_name: string | null;
  plate?: string | null;
  direction?: PlateDirection | null;
  zone_name?: string | null;
  absence_duration_sec?: number | null;
}

const formatTimestamp = (value: string): string => {
  const date = new Date(value.replace(' ', 'T'));
  return Number.isNaN(date.getTime()) ? value : date.toLocaleString('ru-RU');
};

const ModelCategory: React.FC = () => {
  const { categoryId } = useParams<{ categoryId: string }>();
  const navigate = useNavigate();
  const { requireSubscription } = useSubscription();
  const { categories, models, cameras: catalogCameras, loading: catalogLoading } = useModelsCatalog();
  const { areas } = useAreasNav();
  const category = categories.find((item) => item.id === categoryId);
  const categoryModels = useMemo(
    () => models.filter((model) => model.category === categoryId),
    [models, categoryId]
  );
  const categoryModelNames = useMemo(
    () => new Set(categoryModels.map((model) => model.name.toLowerCase())),
    [categoryModels]
  );
  const categoryCameras = useMemo(
    () => catalogCameras.filter((camera) => cameraHasCatalogModel(camera.models, categoryModelNames)),
    [catalogCameras, categoryModelNames]
  );
  const modelTitleByName = useMemo(
    () => new Map(categoryModels.map((model) => [model.name.toLowerCase(), model.title])),
    [categoryModels]
  );
  const isPlateCategory = useMemo(
    () => categoryModels.some((model) => isLicensePlateModel(model.name)),
    [categoryModels]
  );
  const isPresenceCategory = categoryId !== 'people' && categoryModels.some((model) => isPresenceCheckModel(model.name));

  const [cameras, setCameras] = useState<CategoryCamera[]>([]);
  const [camerasLoading, setCamerasLoading] = useState(true);
  const [activeCameraId, setActiveCameraId] = useState<number | null>(null);
  const [logs, setLogs] = useState<CategoryLog[]>([]);
  const [logsLoading, setLogsLoading] = useState(false);
  const [logsError, setLogsError] = useState('');
  const [page, setPage] = useState(1);
  const [dateFrom, setDateFrom] = useState('');
  const [dateTo, setDateTo] = useState('');
  const [appliedDateFrom, setAppliedDateFrom] = useState('');
  const [appliedDateTo, setAppliedDateTo] = useState('');
  const [plateDraft, setPlateDraft] = useState('');
  const [appliedPlate, setAppliedPlate] = useState('');
  const [logCameraId, setLogCameraId] = useState<number | null>(null);
  const [logModel, setLogModel] = useState('');
  const [photoLog, setPhotoLog] = useState<CategoryLog | null>(null);
  const videoRef = useRef<HTMLDivElement | null>(null);

  useEffect(() => {
    let cancelled = false;
    const loadCameras = async () => {
      setCamerasLoading(true);
      try {
        const areaIds = Array.from(new Set(categoryCameras.map((camera) => camera.area_id)));
        const responses = await Promise.all(areaIds.map(async (areaId) => {
          try { return await camerasAPI.getByArea(areaId, categoryId); } catch { return null; }
        }));
        if (cancelled) return;
        const allowedIds = new Set(categoryCameras.map((camera) => camera.id));
        const resolved: CategoryCamera[] = [];
        responses.forEach((response, index) => {
          if (!response?.success) return;
          const areaId = areaIds[index];
          const areaName = areas.find((area) => area.id === areaId)?.name || 'Участок';
          response.cameras.forEach((camera) => {
            if (camera.id == null || !allowedIds.has(camera.id)) return;
            resolved.push({
              id: camera.id,
              name: camera.name,
              areaId,
              areaName,
              sourceId: camera.camera_id,
              models: camera.models || [],
              status: camera.status || {},
              isOnline: camera.is_online !== false,
              webrtcOut: camera.is_paid === false ? null : camera.webrtc_out ?? null,
              isPaid: camera.is_paid !== false,
              paidUntil: camera.paid_until ?? null,
            });
          });
        });
        setCameras(resolved);
        setActiveCameraId((current) => resolved.some((camera) => camera.id === current) ? current : resolved[0]?.id ?? null);
      } finally {
        if (!cancelled) setCamerasLoading(false);
      }
    };
    if (!catalogLoading) void loadCameras();
    return () => { cancelled = true; };
  }, [categoryCameras, catalogLoading, areas]);

  const activeCamera = useMemo(
    () => cameras.find((camera) => camera.id === activeCameraId) ?? null,
    [cameras, activeCameraId]
  );
  const activeNeedsLprRoi = Boolean(
    activeCamera &&
    isPlateCategory &&
    activeCamera.isPaid &&
    activeCamera.isOnline &&
    hasLicensePlateRoi(activeCamera.status, activeCamera.models) === false
  );

  const requestRef = useRef(0);
  const inFlightRef = useRef(false);
  const offsetsRef = useRef(new Map<number, number>([[1, 0]]));
  const [hasMore, setHasMore] = useState(false);

  const loadLogs = useCallback(async (nextPage: number, quiet = false) => {
    if (!categoryId || (quiet && inFlightRef.current)) return;
    const requestId = ++requestRef.current;
    inFlightRef.current = true;
    try {
      if (!quiet) setLogsLoading(true);
      const response = await logsAPI.getByCategory({
        categoryId, page: nextPage, perPage: PAGE_SIZE,
        offset: offsetsRef.current.get(nextPage) ?? (nextPage - 1) * PAGE_SIZE,
        cameraId: logCameraId ?? undefined, models: logModel ? [logModel] : undefined,
        plate: appliedPlate || undefined,
        dateFrom: appliedDateFrom || undefined, dateTo: appliedDateTo || undefined,
      });
      if (requestId !== requestRef.current) return;
      if (!response.success) throw new Error('Не удалось загрузить журнал');
      setLogs(previous => quiet ? mergeCategoryLogs(previous, response.logs, PAGE_SIZE) : response.logs);
      setHasMore(response.has_more);
      offsetsRef.current.set(nextPage + 1, response.next_offset);
      if (!quiet) setPage(nextPage);
      setLogsError('');
    } catch (error) {
      if (requestId === requestRef.current) setLogsError(error instanceof Error ? error.message : 'Не удалось загрузить журнал');
    } finally {
      if (requestId === requestRef.current) { inFlightRef.current = false; setLogsLoading(false); }
    }
  }, [categoryId, logCameraId, logModel, appliedDateFrom, appliedDateTo, appliedPlate]);

  useEffect(() => {
    offsetsRef.current = new Map([[1, 0]]);
    void loadLogs(1);
    return () => { requestRef.current++; inFlightRef.current = false; };
  }, [loadLogs]);
  useEffect(() => {
    const timer = window.setInterval(() => {
      if (page === 1 && document.visibilityState === 'visible') void loadLogs(1, true);
    }, POLL_MS);
    return () => window.clearInterval(timer);
  }, [page, loadLogs]);
  useEffect(() => {
    const container = videoRef.current;
    if (!container || !activeCamera?.webrtcOut || activeNeedsLprRoi) return;
    let streamUrl = activeCamera.webrtcOut;
    if (streamUrl.startsWith('https://') && streamUrl.includes('192.168.')) streamUrl = streamUrl.replace('https://', 'http://');
    const iframe = document.createElement('iframe');
    iframe.src = streamUrl;
    iframe.allow = 'autoplay; fullscreen';
    iframe.setAttribute('allowfullscreen', 'true');
    container.appendChild(iframe);
    return () => iframe.remove();
  }, [activeCamera?.webrtcOut, activeNeedsLprRoi]);

  if (!catalogLoading && (!category || categoryCameras.length === 0)) return <Navigate to="/dashboard" replace />;

  const filtersActive = !!appliedDateFrom || !!appliedDateTo || (isPlateCategory && !!appliedPlate);
  const applyFilters = (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    const plate = isPlateCategory ? plateDraft.trim() : '';
    if (plate === appliedPlate && dateFrom === appliedDateFrom && dateTo === appliedDateTo) {
      void loadLogs(1);
    }
    setAppliedPlate(plate);
    setAppliedDateFrom(dateFrom);
    setAppliedDateTo(dateTo);
    setPage(1);
  };

  return (
    <div className="plates-page model-category-page">
      <div className="container ui-page">
      <nav className="ui-breadcrumbs" aria-label="Навигация">
        <Link to="/dashboard">Главная</Link><span aria-hidden="true">/</span><span>{category?.title || 'Категория'}</span>
      </nav>
      <header className="ui-page-head">
        <div className="ui-page-head__text">
          <h1 className="ui-page-head__title">{category?.title || 'Загрузка…'}</h1>
          <p className="ui-page-head__subtitle">Камеры и журнал детекций по категории</p>
        </div>
      </header>

      {logsError && <div className="ui-alert ui-alert--danger plates-error"><span>{logsError}</span><button className="ui-btn ui-btn--outline ui-btn--sm" onClick={() => void loadLogs(page)}>Повторить</button></div>}

      <div className="plates-layout">
        <section className="plates-camera">
          <header className="plates-camera__head">
            <h2 className="ui-section-title">Камера</h2>
            {cameras.length > 1 && (
              <select className="ui-select plates-camera__select" value={activeCameraId ?? ''} onChange={(event) => { setActiveCameraId(Number(event.target.value)); setPage(1); }}>
                {cameras.map((camera) => <option key={camera.id} value={camera.id}>{camera.name} — {camera.areaName}</option>)}
              </select>
            )}
          </header>
          <div className="plates-camera__video" ref={videoRef}>
            {activeCamera && !activeCamera.isPaid ? (
              <div className="plates-camera__placeholder plates-camera__placeholder--unpaid model-category-video-blocker">
                <span className="plates-camera__unpaid-title">{activeCamera.paidUntil ? 'Оплата камеры закончилась' : 'Камера не оплачена'}</span>
                <span className="model-category-video-blocker__text">Для просмотра потока и работы аналитики необходимо оплатить камеру.</span>
                <button className="ui-btn ui-btn--primary ui-btn--sm" onClick={() => navigate('/balance')}>Оплатить</button>
              </div>
            ) : activeNeedsLprRoi ? (
              <div className="plates-camera__placeholder plates-camera__placeholder--unpaid model-category-video-blocker">
                <span className="plates-camera__unpaid-title">Задайте зону распознавания номеров</span>
                <span className="model-category-video-blocker__text">Без зоны ROI распознавание номеров работает неточно, поэтому камера пока выключена.</span>
                <button className="ui-btn ui-btn--primary ui-btn--sm" onClick={() => navigate(`/area/${activeCamera?.areaId}`)}>Задать зону ROI</button>
              </div>
            ) : !activeCamera?.webrtcOut && <div className="plates-camera__placeholder" />}
            <div className="plates-camera__overlay">
              <div className="plates-camera__top"><span className="plates-camera__label">{camerasLoading ? 'Загрузка камеры…' : activeCamera?.name ?? 'Камера не найдена'}</span></div>
              {activeCamera?.webrtcOut && !activeNeedsLprRoi && activeCamera.isPaid && <div className="plates-camera__bottom"><span className="plates-camera__live-dot" /><span>В эфире</span></div>}
            </div>
          </div>
          {!camerasLoading && activeCamera && activeCamera.isPaid && !activeCamera.webrtcOut && <p className="plates-camera__hint">Живой поток недоступен. <button className="ui-btn ui-btn--link" onClick={() => requireSubscription(() => navigate(`/area/${activeCamera.areaId}`))}>Открыть участок</button></p>}
        </section>

        <section className="plates-journal ui-card">
          <header className="plates-journal__head">
            <div className="plates-journal__title"><h2 className="ui-card__title">Детекции</h2>{page === 1 && !filtersActive && <span className="plates-journal__live"><span className="plates-journal__live-dot" />Обновляется автоматически</span>}</div>
            <span className="plates-journal__page">Стр. {page}</span>
          </header>
          <form className="plates-filters model-category-filters" onSubmit={applyFilters} autoComplete="off">
            {isPlateCategory && <div className="plates-filters__plate">
              <input className="ui-input" type="text" placeholder="Поиск по номеру" aria-label="Поиск по номеру" maxLength={32} spellCheck={false} value={plateDraft} onChange={(event) => setPlateDraft(event.target.value)} />
            </div>}
            <select
              className="ui-select model-category-filter-select"
              value={logCameraId ?? ''}
              onChange={(event) => { setLogCameraId(event.target.value ? Number(event.target.value) : null); setPage(1); }}
              aria-label="Фильтр по камере"
            >
              <option value="">Все камеры</option>
              {cameras.map((camera) => <option key={camera.id} value={camera.id}>{camera.name} — {camera.areaName}</option>)}
            </select>
            {!isPlateCategory && (
              <select
                className="ui-select model-category-filter-select"
                value={logModel}
                onChange={(event) => { setLogModel(event.target.value); setPage(1); }}
                aria-label="Фильтр по модели"
              >
                <option value="">Все модели</option>
                {categoryModels.map((model) => <option key={model.name} value={model.name}>{model.title}</option>)}
              </select>
            )}
            <LogsDateRangePicker dateFrom={dateFrom} dateTo={dateTo} onChange={(from, to) => { setDateFrom(from); setDateTo(to); }} />
            <button type="submit" className="ui-btn ui-btn--primary ui-btn--sm">Найти</button>
            {filtersActive && <button type="button" className="ui-btn ui-btn--outline ui-btn--sm" onClick={() => { setPlateDraft(''); setAppliedPlate(''); setDateFrom(''); setDateTo(''); setAppliedDateFrom(''); setAppliedDateTo(''); setPage(1); }}>Сбросить</button>}
          </form>
          {logsLoading ? <div className="ui-loading">Загрузка журнала…</div> : logs.length === 0 ? (
            <div className="ui-empty"><span className="ui-empty__title">{filtersActive ? 'Ничего не найдено' : 'Детекций пока нет'}</span><span>{filtersActive ? 'Измените условия поиска или сбросьте фильтры' : 'Записи появятся после срабатывания выбранной модели'}</span></div>
          ) : (
            <div className="ui-table-wrap"><table className="ui-table plates-table"><thead><tr>{isPlateCategory ? <><th>Время</th><th>Номер</th><th>Направление</th><th>Камера</th><th>Фото</th></> : <><th>Время</th><th>Модель / событие</th>{isPresenceCategory && <><th>Рабочее место</th><th>Длительность</th></>}<th>Камера</th><th>Фото</th></>}</tr></thead><tbody>{logs.map((log) => isPlateCategory ? (
              <tr key={log.id}><td className="plates-table__time">{formatTimestamp(log.timestamp)}</td><td>{log.plate ? <span className="plates-table__plate">{log.plate}</span> : <span className="plates-table__dash">—</span>}</td><td className="plates-table__direction">{log.direction ? <DirectionBadge direction={log.direction} /> : <span className="plates-table__dash">—</span>}</td><td className="plates-table__camera"><span className="plates-table__camera-name">{log.camera_name || '—'}</span></td><td>{log.screen_url ? <button className="ui-btn ui-btn--outline ui-btn--sm" onClick={() => setPhotoLog(log)}>Просмотр</button> : <span className="plates-table__dash">—</span>}</td></tr>
            ) : (
              <tr key={log.id}><td className="plates-table__time">{formatTimestamp(log.timestamp)}</td><td><span className="model-category-model"><ModelIcon model={log.model} size={16} />{modelTitleByName.get(log.model.toLowerCase()) || getModelDisplayName(log.model)}</span></td>{isPresenceCategory && <><td>{log.zone_name ?? ((log.model === 'absence' || log.model === 'absence_end') ? 'Весь кадр' : '—')}</td><td>{log.absence_duration_sec == null ? '—' : `${log.absence_duration_sec} с`}</td></>}<td className="plates-table__camera"><span className="plates-table__camera-name">{log.camera_name || '—'}</span></td><td>{log.screen_url ? <button className="ui-btn ui-btn--outline ui-btn--sm" onClick={() => setPhotoLog(log)}>Просмотр</button> : <span className="plates-table__dash">—</span>}</td></tr>
            ))}</tbody></table></div>
          )}
          {(hasMore || page > 1) && <footer className="plates-journal__footer"><button className="ui-btn ui-btn--outline ui-btn--sm" disabled={page <= 1 || logsLoading} onClick={() => void loadLogs(page - 1)}>Назад</button><span className="plates-journal__range">Страница {page}{logs.length === 0 ? ' · нет событий по выбранным фильтрам' : ''}</span><button className="ui-btn ui-btn--outline ui-btn--sm" disabled={!hasMore || logsLoading} onClick={() => void loadLogs(page + 1)}>Вперёд</button></footer>}
        </section>
      </div>
      </div>

      <DetectionPhotoModal isOpen={!!photoLog} photoUrl={photoLog?.screen_url ?? null} title="Фотофиксация детекции" meta={photoLog ? <>{isPlateCategory ? <><span className="area-modal__photo-meta-label">Номер</span><span className="plates-table__plate">{photoLog.plate || '—'}</span>{photoLog.direction && <><span className="area-modal__photo-meta-label">Направление</span><DirectionBadge direction={photoLog.direction} /></>}</> : <><span className="area-modal__photo-meta-label">Модель</span><span>{modelTitleByName.get(photoLog.model.toLowerCase()) || getModelDisplayName(photoLog.model)}</span></>}<span className="area-modal__photo-meta-label">Камера</span><span>{photoLog.camera_name || '—'}</span></> : null} onClose={() => setPhotoLog(null)} />
    </div>
  );
};

export default ModelCategory;
